import { UserRoleType } from '@order-management/types';
import { ReactNode, useCallback, useMemo } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaArrowsRotate, FaMasksTheater } from 'react-icons/fa6';
import ManagementHubIcon from '../../../assets/icons/management-hub-icon';
import Button from '../../ui/button/button';
import { switchRole } from '../../users/users-service';
import AdminToolbar from '../admin-toolbar/admin-toolbar';
import useHttpRequest from '../hooks/http-request';
import useisMasqueradingCustomerUser from '../hooks/is-masquerading-customer-user';
import useUser from '../hooks/user';
import useUserHasZspaceStaffPermissions from '../hooks/user-has-zspace-staff-permissions';
import If from '../if/if';
import styles from './home-header.module.scss';

const SWITCH_ROLE_ERROR_MESSAGE = 'Could not switch role. Please try again';

export type HomeHeaderProps = {
  children?: ReactNode;
};

export function HomeHeader({ children }: HomeHeaderProps) {
  const { user } = useUser();
  const hasZspaceStaffPermissions = useUserHasZspaceStaffPermissions();
  const isMasqueradingCustomerUser = useisMasqueradingCustomerUser();
  const { executeHttpRequest, isLoading } = useHttpRequest();

  const onSwitchRole = useCallback(() => {
    return executeHttpRequest({
      asyncFunction: async () => {
        const userRoleToSwitch = user.roles.find(
          (roleAssignment) =>
            roleAssignment.role.name === UserRoleType.ZSPACE_INTERNAL
        );
        await switchRole(userRoleToSwitch!.id);
        window.location.replace('/');
      },
      customErrorMessage: SWITCH_ROLE_ERROR_MESSAGE,
    });
  }, [executeHttpRequest, user.roles]);

  const title = useMemo(() => {
    if (isMasqueradingCustomerUser) {
      return 'You are masquerading as a customer';
    }
    return 'Welcome to zSpace Management Hub';
  }, [isMasqueradingCustomerUser]);

  const titleClassName = useMemo(() => {
    const baseClassName = `is-size-3 ${styles.title}`;
    if (isMasqueradingCustomerUser) {
      return `${baseClassName} has-text-weight-bold has-text-danger-dark`;
    }
    return `${baseClassName} has-text-weight-light has-text-primary-dark`;
  }, [isMasqueradingCustomerUser]);

  return (
    <Columns display="flex" flexDirection="column" className="is-flex-grow-1">
      <If condition={hasZspaceStaffPermissions}>
        <Columns.Column
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-end"
        >
          <AdminToolbar />
        </Columns.Column>
      </If>
      <If condition={isMasqueradingCustomerUser}>
        <Columns.Column
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-end"
        >
          <Button
            className="is-borderless hoverable-primary-dark"
            color="transparent"
            onClick={onSwitchRole}
            isExecutingAction={isLoading}
          >
            <Button.LoadingIcon
              iconColor="primary-dark"
              icon={FaArrowsRotate}
            />
            <span className="is-underlined has-text-primary-dark">
              Manage my own orders
            </span>
          </Button>
        </Columns.Column>
      </If>

      <Columns.Column
        display="flex"
        flexDirection="column"
        alignContent="flex-end"
        alignItems="center"
        justifyContent="flex-end"
      >
        <If condition={isMasqueradingCustomerUser}>
          <Icon color={'primary-dark'} className={styles.masqueradeModeIcon}>
            <FaMasksTheater />
          </Icon>
        </If>
        <h3
          className={`is-size-6 has-text-weight-normal has-text-primary-dark mb-5 ${styles.email}`}
        >
          {user.email}
        </h3>
        <Element display="flex" alignItems="center" mb={5}>
          <If condition={!isMasqueradingCustomerUser}>
            <Icon className={styles.managementHubIcon} mr={4}>
              <ManagementHubIcon width={20} />
            </Icon>
          </If>
          <h1 className={titleClassName}>{title}</h1>
        </Element>
        {children}
      </Columns.Column>
    </Columns>
  );
}

export default HomeHeader;
